import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Brain, Loader, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import './DocumentAssistant.css';
import { useApiWithAuth } from '../hooks/useApiWithAuth';
import { API_BASE_URL } from '../config';

const DocumentAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documentSummary, setDocumentSummary] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const { fetchWithAuth } = useApiWithAuth();

  // Fixed suggestion bubbles that are always available
  const fixedSuggestions = [
    "What is the percentage of invoices that have passed DQ checks",
    "How many invoices Passed the dq checks",
    "What is the total invoice due amount of invoices that passed DQ checks",
    "List invoices that failed DQ checks",
    "How many invoices failed the dq checks",
    "what is the tracking number of the invoice GLE-2025-009567",
    "How many documents were processed",
  ];

  // Load document summary and suggestions on component mount
  /* eslint-disable */
  useEffect(() => {
    loadDocumentSummary();
    loadQuestionSuggestions();
  }, []);

  const loadDocumentSummary = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/document-summary`);
      const result = await response.json();
      
      if (result.success) {
        setDocumentSummary(result.summary);
        
        // Add welcome message based on document availability
        if (result.summary.total_chunks > 0) {
          setMessages([
            {
              id: 1,
              type: 'assistant',
              text: `👋 Welcome! I can help you analyze all processed documents. Currently, I have access to:

📊 **Document Statistics**
• ${result.summary.total_chunks} total information chunks
• ${result.summary.metadata_chunks} metadata chunks (vendor info, headers, etc.)
• ${result.summary.data_chunks} data chunks (line items, transactions, etc.)

💡 Try asking me about:
• Invoice details and totals
• Vendor information
• Line items and products
• Payment terms and dates
• Or use the suggestion bubbles below!`,
              timestamp: new Date().toISOString()
            }
          ]);
        } else {
          setMessages([
            {
              id: 1,
              type: 'assistant',
              text: 'Welcome! I can help you analyze documents once they are processed. Upload and process a PDF document through the main upload section first, then come back to chat with me about its content.',
              timestamp: new Date().toISOString()
            }
          ]);
        }
      }
    } catch (error) {
      console.error('Error loading document summary:', error);
      setMessages([
        {
          id: 1,
          type: 'assistant',
          text: 'Hello! I\'m your document assistant. I can help you analyze processed documents, but I\'m having trouble connecting to the server right now.',
          timestamp: new Date().toISOString()
        }
      ]);
    }
  };

  const loadQuestionSuggestions = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/question-suggestions`);
      const result = await response.json();
      
      if (result.success) {
        setSuggestions(result.suggestions.slice(0, 6)); // Limit to 6 suggestions
      }
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const sendChatMessage = async (message) => {
    setIsLoading(true);
    
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });

      const result = await response.json();

      if (result.success) {
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          text: result.response.answer,
          timestamp: new Date().toISOString(),
          sources: result.response.sources,
          searchType: result.response.search_type,
          numChunks: result.response.num_chunks_used
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          text: `❌ Sorry, I encountered an error: ${result.message}`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        text: '❌ Sorry, I\'m having trouble connecting to the server. Please make sure the API server is running.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputValue;
    setInputValue('');

    // Send to chat API
    await sendChatMessage(messageToSend);
  };

  const handleQuickSend = async (suggestion) => {
    if (isLoading) return;
    
    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: suggestion,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    
    // Send to chat API
    await sendChatMessage(suggestion);
  };

  return (
    <div className="document-assistant">
      <div className="assistant-header">
        <div className="header-content">
          <div className="header-left">
            <MessageCircle size={20} color="#0084ff" />
            <div className="header-text">
              <h3>DocuAI</h3>
              <span className="header-subtitle">Your document Q&A specialist</span>
            </div>
          </div>
          {/*documentSummary && documentSummary.total_chunks > 0 && (
            <div className="header-stats">
              <div className="mini-stat">
                <span className="mini-stat-value">{documentSummary.metadata_chunks} Meta</span>
              </div>
              <span style={{ color: '#65676b' }}>•</span>
              <div className="mini-stat">
                <span className="mini-stat-value">{documentSummary.data_chunks} Data</span>
              </div>
            </div>
          )*/}
        </div>
      </div>

      <div className="quick-suggestions">
        {fixedSuggestions.map((suggestion, index) => (
          <button
            key={index}
            className="suggestion-bubble"
            onClick={() => handleQuickSend(suggestion)}
            disabled={isLoading}
          >
            <Sparkles size={16} />
            {suggestion}
          </button>
        ))}
        {suggestions.map((suggestion, index) => (
          <button
            key={`dynamic-${index}`}
            className="suggestion-bubble"
            onClick={() => handleQuickSend(suggestion)}
            disabled={isLoading}
          >
            <Brain size={16} />
            {suggestion}
          </button>
        ))}
      </div>

      <div className="messages">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  // Custom styling for different markdown elements
                  h1: ({node, ...props}) => <h1 style={{color: '#1a1a1a', marginBottom: '0.5rem'}} {...props} />,
                  h2: ({node, ...props}) => <h2 style={{color: '#1a1a1a', marginBottom: '0.5rem'}} {...props} />,
                  h3: ({node, ...props}) => <h3 style={{color: '#1a1a1a', marginBottom: '0.5rem'}} {...props} />,
                  p: ({node, ...props}) => <p style={{marginBottom: '0.5rem', lineHeight: '1.5'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{marginLeft: '1rem', marginBottom: '0.5rem'}} {...props} />,
                  ol: ({node, ...props}) => <ol style={{marginLeft: '1rem', marginBottom: '0.5rem'}} {...props} />,
                  li: ({node, ...props}) => <li style={{marginBottom: '0.25rem'}} {...props} />,
                  strong: ({node, ...props}) => <strong style={{color: '#0084ff', fontWeight: '600'}} {...props} />,
                  em: ({node, ...props}) => <em style={{fontStyle: 'italic'}} {...props} />,
                  code: ({node, inline, ...props}) => 
                    inline ? (
                      <code style={{
                        backgroundColor: '#f6f8fa', 
                        padding: '2px 4px', 
                        borderRadius: '3px',
                        fontSize: '0.9em',
                        fontFamily: 'Monaco, Consolas, "Liberation Mono", Courier, monospace'
                      }} {...props} />
                    ) : (
                      <code {...props} />
                    ),
                  pre: ({node, ...props}) => (
                    <pre style={{
                      backgroundColor: '#f6f8fa',
                      padding: '1rem',
                      borderRadius: '6px',
                      overflow: 'auto',
                      fontSize: '0.9em',
                      fontFamily: 'Monaco, Consolas, "Liberation Mono", Courier, monospace',
                      border: '1px solid #e1e4e8',
                      marginBottom: '0.5rem'
                    }} {...props} />
                  ),
                  table: ({node, ...props}) => (
                    <div style={{overflowX: 'auto', marginBottom: '0.5rem'}}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.9em'
                      }} {...props} />
                    </div>
                  ),
                  th: ({node, ...props}) => (
                    <th style={{
                      border: '1px solid #e1e4e8',
                      padding: '8px 12px',
                      backgroundColor: '#f6f8fa',
                      fontWeight: '600',
                      textAlign: 'left'
                    }} {...props} />
                  ),
                  td: ({node, ...props}) => (
                    <td style={{
                      border: '1px solid #e1e4e8',
                      padding: '8px 12px'
                    }} {...props} />
                  ),
                  blockquote: ({node, ...props}) => (
                    <blockquote style={{
                      borderLeft: '4px solid #0084ff',
                      paddingLeft: '1rem',
                      margin: '0 0 0.5rem 0',
                      fontStyle: 'italic',
                      color: '#586069'
                    }} {...props} />
                  )
                }}
              >
                {message.text}
              </ReactMarkdown>
            </div>
            {/* {message.sources && message.sources.length > 0 && (
              <div className="message-sources">
                <small>📚 Sources: {message.sources.join(', ')}</small>
              </div>
            )} */}
            {/* {message.numChunks && (
              <div className="message-meta">
                <small>🔍 Searched {message.numChunks} document chunks</small>
              </div>
            )} */}
            <div className="message-time">
              {new Date(message.timestamp).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message assistant">
            <div className="message-content loading">
              <Loader size={16} className="spinning" />
              <span>Analyzing documents...</span>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSendMessage} className="message-input-wrapper">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={documentSummary && documentSummary.total_chunks > 0 
            ? "Ask me anything about your processed documents..." 
            : "Process a document first to start chatting..."
          }
          className="message-input"
          disabled={isLoading}
        />
        <button 
          type="submit" 
          className="send-button"
          disabled={!inputValue.trim() || isLoading}
        >
          {isLoading ? 
            <Loader size={18} className="spinning" style={{ color: '#0084ff' }} /> : 
            <Send size={18} style={{ color: '#0084ff' }} />
          }
        </button>
      </form>
    </div>
  );
};

export default DocumentAssistant;